import { isNil } from "lodash";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
} from "react";
import { useForm, useFormContext } from "react-hook-form";

import { useDropdownSourceContext } from "@ryde/redux";
import {
  getAdditionEntityName,
  groupAdditionEntitiesByPrice,
  IAdditionEntity,
  IDropdownItem,
} from "@ryde/shared";

import {
  IPassengerAdditionDraftForm,
  IPassengerTier,
  IPricingReasonDto,
} from "./passenger-addition.models";
import {
  mapEntityPricesToReasonDto,
  mapPricesToAdditionsByReasons,
  sortPassengerTiers,
} from "./passenger-addition.utils";

export const usePassengerAddition = (name: string) => {
  const outerMethods = useFormContext();
  outerMethods.register(name);
  const pricingFieldName = `${name}.prices`;

  const draftMethods = useForm<IPassengerAdditionDraftForm>({
    defaultValues: {
      tierFrom: undefined,
      tierPrice: undefined,
      reasonsExtraSelect: [],
      extraPrice: undefined,
    },
  });

  // --- Tier state ---
  const tiersFieldName = `${name}.passengerBasedPricing`;
  const [tiers, setTiers] = useState<IPassengerTier[]>(
    sortPassengerTiers(outerMethods?.getValues(tiersFieldName) || []),
  );
  const tiersSnapshot = useRef<IPassengerTier[]>([]);

  const handleAddTier = useCallback((): void => {
    const from = draftMethods.getValues("tierFrom");
    const price = draftMethods.getValues("tierPrice");
    if (isNil(from) || isNil(price)) {
      return;
    }

    setTiers(prev => {
      const duplicate = prev.some(tier => tier.from === Number(from));
      if (duplicate) {
        return prev;
      }
      const newTiers = sortPassengerTiers([
        ...prev,
        { from: Number(from), price: Number(price) },
      ]);
      const currentPricing = outerMethods.getValues(name) || {};
      outerMethods.setValue(
        name,
        { ...currentPricing, passengerBasedPricing: newTiers },
        { shouldDirty: true },
      );
      return newTiers;
    });
    draftMethods.resetField("tierFrom");
    draftMethods.resetField("tierPrice");
  }, [draftMethods, outerMethods, name]);

  const handleEditTier = useCallback(
    (from: number, price: number): void => {
      setTiers(prev => {
        tiersSnapshot.current = prev;
        return prev.filter(tier => tier.from !== from);
      });
      draftMethods.setValue("tierFrom", from);
      draftMethods.setValue("tierPrice", price);
    },
    [draftMethods],
  );

  const handleClearTier = useCallback(
    (from: number): void => {
      setTiers(prev => {
        const filtered = prev.filter(tier => tier.from !== from);
        const currentPricing = outerMethods.getValues(name) || {};
        outerMethods.setValue(
          name,
          { ...currentPricing, passengerBasedPricing: filtered },
          { shouldDirty: true },
        );
        return filtered;
      });
    },
    [outerMethods, name],
  );

  const handleClearTierForm = useCallback((): void => {
    setTiers(prev =>
      tiersSnapshot.current.length > 0 ? tiersSnapshot.current : prev,
    );
    draftMethods.resetField("tierFrom");
    draftMethods.resetField("tierPrice");
  }, [draftMethods]);

  // --- Reason state ---
  const [specificReasons, setSpecificReasons] = useState<IAdditionEntity[]>([]);
  const [reasons, setReasons] = useState<IDropdownItem[]>([]);
  const reasonsSnapshot = useRef<IAdditionEntity[]>([]);

  const { getDropdownItems } = useDropdownSourceContext();
  const [, startTransition] = useTransition();

  useEffect(() => {
    const getReasons = async (): Promise<void> => {
      const items = await getDropdownItems?.("reason");

      setReasons(items || []);

      const prices = outerMethods?.getValues(pricingFieldName) || [];

      setSpecificReasons(
        mapPricesToAdditionsByReasons(prices as IPricingReasonDto[], items),
      );
    };

    startTransition(() => {
      getReasons();
    });
  }, [getDropdownItems, outerMethods, pricingFieldName]);

  const handleAddSpecificReason = useCallback((): void => {
    const selectedReasons = draftMethods.getValues("reasonsExtraSelect");
    const price = draftMethods.getValues("extraPrice");

    setSpecificReasons(prev => {
      const newValue = [
        ...prev,
        ...(selectedReasons?.map((reasonId: number) => ({
          id: reasonId,
          name: getAdditionEntityName(reasonId, reasons),
          price: Number(price),
        })) || []),
      ];
      const currentPricing = outerMethods.getValues(name) || {};
      outerMethods.setValue(
        name,
        { ...currentPricing, prices: mapEntityPricesToReasonDto(newValue) },
        { shouldDirty: true },
      );
      return newValue;
    });
    draftMethods.resetField("reasonsExtraSelect");
    draftMethods.resetField("extraPrice");
  }, [reasons, draftMethods, outerMethods, name]);

  const handleClearSpecificReasonForm = useCallback((): void => {
    setSpecificReasons(prev =>
      reasonsSnapshot.current.length > 0 ? reasonsSnapshot.current : prev,
    );
    draftMethods.resetField("reasonsExtraSelect");
    draftMethods.resetField("extraPrice");
  }, [draftMethods]);

  const handleEditSpecificReason = useCallback(
    (ids: number[], price: number): void => {
      setSpecificReasons(prev => {
        reasonsSnapshot.current = prev;
        return (prev || []).filter(reason => reason.price !== price);
      });
      draftMethods.setValue("reasonsExtraSelect", ids);
      draftMethods.setValue("extraPrice", price);
    },
    [draftMethods],
  );

  const handleClearSpecificReason = useCallback(
    (price: number): void => {
      const filteredReasons = (specificReasons || []).filter(
        reason => reason.price !== price,
      );

      setSpecificReasons(filteredReasons);
      const currentPricing = outerMethods.getValues(name) || {};
      outerMethods.setValue(
        name,
        {
          ...currentPricing,
          prices: mapEntityPricesToReasonDto(filteredReasons),
        },
        { shouldDirty: true },
      );
    },
    [specificReasons, name, outerMethods],
  );

  const groupedEntities = useMemo((): IAdditionEntity[][] => {
    return specificReasons?.length
      ? Object.values(groupAdditionEntitiesByPrice(specificReasons))
      : [];
  }, [specificReasons]);

  const dropdownItems = useMemo(() => {
    return (reasons || []).filter(
      reason => !specificReasons?.find?.(a => a.id === reason.value),
    );
  }, [reasons, specificReasons]);

  return {
    tiers,
    draftMethods,
    handleAddTier,
    handleEditTier,
    handleClearTier,
    handleClearTierForm,
    specificReasons,
    groupedEntities,
    dropdownItems,
    handleAddSpecificReason,
    handleEditSpecificReason,
    handleClearSpecificReason,
    handleClearSpecificReasonForm,
  };
};
